import {
  Role,
  useAddPermissionToRoleMutation,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useRemovePermissionToRoleMutation,
  useSetRolesForPersonMutation,
  useUpdateRoleMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseRoleMutationProps = {}

export type UseRoleMutationResult = {
  createRole: (role: Partial<Role>, onSuccess: ((role: Role) => void) | undefined) => void
  updateRole: (role: Partial<Role>, onSuccess: (() => void) | undefined) => void
  setRoles: (roleIds: string[], personId: string, onSuccess: (() => void) | undefined) => void
  deleteRole: (roleId: string, onSuccess: (() => void) | undefined) => void
  addPermission: (roleId: string, permissionId: string, onSuccess: (() => void) | undefined) => void
  removePermission: (roleId: string, permissionId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isSettingRoles?: boolean
  isAddingPermission?: boolean
  isRemovingPermission?: boolean
}

export const useRoleMutation = (props: UseRoleMutationProps = {}): UseRoleMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)
  const [_settingRoles, _setSettingRoles] = useDebouncedState(false, 500)
  const [_addingPermission, _setAddingPermission] = useDebouncedState(false, 500)
  const [_removingPermission, _setRemovingPermission] = useDebouncedState(false, 500)

  // possible mutations
  const [_createRole, {isLoading: isCreating}] = useCreateRoleMutation()
  const [_updateRole, {isLoading: isUpdating}] = useUpdateRoleMutation()
  const [_deleteRole, {isLoading: isDeleting}] = useDeleteRoleMutation()
  const [_setRoles, {isLoading: isSettingRoles}] = useSetRolesForPersonMutation()
  const [_addPermission, {isLoading: isAddingPermission}] = useAddPermissionToRoleMutation()
  const [_removePermission, {isLoading: isRemovingPermission}] = useRemovePermissionToRoleMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setSettingRoles(isSettingRoles)
  }, [isSettingRoles, _setSettingRoles])

  useEffect(() => {
    _setAddingPermission(isAddingPermission)
  }, [isAddingPermission, _setAddingPermission])

  useEffect(() => {
    _setRemovingPermission(isRemovingPermission)
  }, [isRemovingPermission, _setRemovingPermission])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useRoleUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.role", "Role"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useRoleUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating || _addingPermission || _removingPermission) {
      notifications.show({
        id: "useRoleUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.role", "Role"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useRoleUpdate_updating")
    }
  }, [_updating, _addingPermission, _removingPermission, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useRoleUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.role", "Role"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useRoleUpdate_deleting")
    }
  }, [_deleting, t]);

  useEffect(() => {
    if (_settingRoles) {
      notifications.show({
        id: "useRoleUpdate_setting",
        title: t("common.status.setting", "Setting") + " " + t("common.entity.role.plural", "Roles"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useRoleUpdate_setting")
    }
  }, [_settingRoles, t]);

  const handleCreateResult = useCallback((promise: Promise<Role>, onSuccess: ((role: Role) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.role", "Role") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.role", "Role") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.role", "Role") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleSettingResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.role.plural", "Roles") + " " + t("common.status.set", "Set"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((roleId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteRole({id: roleId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteRole, handleDeleteResult])

  const createRole = useCallback((role: Partial<Role>, onSuccess: ((role: Role) => void) | undefined) => {
    const promise = _createRole(role).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createRole, handleCreateResult])

  const updateRole = useCallback((role: Partial<Role>, onSuccess: (() => void) | undefined) => {
    const promise = _updateRole(role).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateRole, handleUpdateResult])

  const deleteRole = useCallback((roleId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.role", "Role"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.role.confirm", "Are you sure you want to delete this role?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(roleId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  const setRoles = useCallback((roleIds: string[], personId: string, onSuccess: (() => void) | undefined) => {
    const promise = _setRoles({roleIds, personId}).unwrap()
    handleSettingResult(promise, onSuccess)
  }, [_setRoles, handleSettingResult])

  const addPermission = useCallback((roleId: string, permissionId: string, onSuccess: (() => void) | undefined) => {
    const promise = _addPermission({roleId, permissionId}).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_addPermission, handleUpdateResult])

  const removePermission = useCallback((roleId: string, permissionId: string, onSuccess: (() => void) | undefined) => {
    const promise = _removePermission({roleId, permissionId}).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_removePermission, handleUpdateResult])

  return {
    createRole: createRole,
    updateRole: updateRole,
    deleteRole: deleteRole,
    setRoles: setRoles,
    addPermission: addPermission,
    removePermission: removePermission,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
    isSettingRoles: _settingRoles,
    isAddingPermission: _addingPermission,
    isRemovingPermission: _removingPermission,
  }
}
